import { useState, useRef } from 'react';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';

import { useHttp } from 'hooks/http.hook';
import { useOnClickOutside } from 'hooks/outsideClick.hook';
import { formatPhoneWithPlus, getPhoneHref } from 'helpers/phone';
import { getVisibleMessengers } from 'helpers/messengers';
import MessengerLink, { MESSAGE_ICON_SIZE } from 'components/common/MessengerLink';
import Loading from 'components/Loading';

import styles from './styles.module.scss';

export default function MessengersPopup({
    children,
    employee,
    className,
    popupPosition = 'right',
    apiRoute,
    contactId,
}) {
    const [open, setOpen] = useState(false);
    const [loadedContacts, setLoadedContacts] = useState(null);
    const ref = useRef();
    const { t } = useTranslation();
    const { request, loading } = useHttp();

    useOnClickOutside(ref, () => setOpen(false));

    const handleOpen = (e) => {
        e.stopPropagation();
        e.preventDefault();

        if (!open) {
            // Если есть API роут, загружаем данные
            if (apiRoute && contactId && !loadedContacts) {
                request(apiRoute.replace(':id', contactId))
                    .then((res) => {
                        setLoadedContacts(res.data);
                    })
                    .catch((err) => {
                        console.log(err.response);
                    });
            }
            setOpen(true);
        }
    };

    const handleClose = (e) => {
        e.stopPropagation();
        setOpen(false);
    };

    // Используем загруженные данные или переданные через props
    const employees = loadedContacts?.employees || [];
    const hasLoadedData = employees.length > 0;

    // Данные из props (для чатов)
    const fullName = [employee?.lastname, employee?.firstname, employee?.middlename]
        .filter(Boolean)
        .join(' ');

    const phone = employee?.mobile_phone || employee?.company?.phone;
    const email = employee?.email || employee?.company?.email;
    const messengers = getVisibleMessengers(employee?.messengers);

    const hasPropsContacts = phone || email || messengers.length > 0;

    // Показываем popup если есть данные или если есть API для загрузки
    const canShowPopup = hasPropsContacts || apiRoute;

    if (!canShowPopup) {
        return <>{children}</>;
    }

    return (
        <div className={cn(styles.wrapper, className)} onClick={handleOpen}>
            {children}
            {open && (
                <div
                    ref={ref}
                    className={cn(styles.popup, styles[`popup--${popupPosition}`])}
                    onClick={(e) => e.stopPropagation()}
                >
                    <button className={styles.closeButton} onClick={handleClose} />

                    {loading ? (
                        <Loading />
                    ) : hasLoadedData ? (
                        // Отображаем загруженные данные (несколько сотрудников)
                        <>
                            <div className={styles.title}>{t('contactsPopup.heading')}:</div>
                            <ul className={styles.list}>
                                {employees.map((emp, index) => {
                                    const empMessengers = getVisibleMessengers(emp.messengers);

                                    return (
                                    <li className={styles.employee} key={index}>
                                        <div className={styles.block}>
                                            <p className={styles.subtitle}>{t('contactsPopup.name')}:</p>
                                            <p className={styles.content}>
                                                {[emp.lastname, emp.firstname, emp.middlename].filter(Boolean).join(' ')}
                                            </p>
                                        </div>

                                        {emp.mobile_phones && emp.mobile_phones.length > 0 && (
                                            <div className={styles.block}>
                                                <p className={styles.subtitle}>{t('contactsPopup.phone')}:</p>
                                                <p className={styles.content}>
                                                    {emp.mobile_phones.map((ph, idx) => (
                                                        <a key={idx} href={getPhoneHref(ph)} className={styles.link}>
                                                            {formatPhoneWithPlus(ph)}{idx < emp.mobile_phones.length - 1 && ', '}
                                                        </a>
                                                    ))}
                                                </p>
                                            </div>
                                        )}

                                        {emp.email && (
                                            <div className={styles.block}>
                                                <p className={styles.subtitle}>{t('contactsPopup.email')}:</p>
                                                <a href={`mailto:${emp.email}`} className={styles.link}>
                                                    {emp.email}
                                                </a>
                                            </div>
                                        )}

                                        {empMessengers.length > 0 && (
                                            <div className={styles.block}>
                                                <p className={styles.subtitle}>{t('contactsPopup.messenger')}:</p>
                                                <div className={styles.messengers}>
                                                    {empMessengers.map((item, idx) => (
                                                        <MessengerLink
                                                            key={idx}
                                                            item={item}
                                                            size={MESSAGE_ICON_SIZE.normal}
                                                        />
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </li>
                                    );
                                })}
                            </ul>
                        </>
                    ) : (
                        // Отображаем данные из props (для чатов)
                        <>
                            {fullName && (
                                <div className={styles.block}>
                                    <p className={styles.subtitle}>{t('contactsPopup.name')}:</p>
                                    <p className={styles.content}>{fullName}</p>
                                </div>
                            )}

                            {phone && (
                                <div className={styles.block}>
                                    <p className={styles.subtitle}>{t('contactsPopup.phone')}:</p>
                                    <a href={getPhoneHref(phone)} className={styles.link}>
                                        {formatPhoneWithPlus(phone)}
                                    </a>
                                </div>
                            )}

                            {email && (
                                <div className={styles.block}>
                                    <p className={styles.subtitle}>{t('contactsPopup.email')}:</p>
                                    <a href={`mailto:${email}`} className={styles.link}>
                                        {email}
                                    </a>
                                </div>
                            )}

                            {messengers.length > 0 && (
                                <div className={styles.block}>
                                    <p className={styles.subtitle}>{t('contactsPopup.messenger')}:</p>
                                    <div className={styles.messengers}>
                                        {messengers.map((item, index) => (
                                            <MessengerLink
                                                key={index}
                                                item={item}
                                                size={MESSAGE_ICON_SIZE.normal}
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
}
